import React, { Fragment, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Intent } from '@blueprintjs/core';
import { Modal, ModalSize, NotificationToaster } from 'components/notifications';
import { FormTextArea, FormToggleList } from 'components/form-fields';
import { Button, ButtonSave } from 'components/buttons';
import { ShowHide } from 'components/layout';
import { useValidation } from 'hooks/useValidation';
import { usePageTitle } from 'hooks/usePageTitle';
import { FeedbackService } from 'services';
import { SubmissionPrivacyNotice } from './SubmissionPrivacyNotice';
import { SubmissionContactSection, contactNameMaxLength, phoneNumberMaxLength } from './SubmissionContactSection';

const defaultFunction = () => {};
const commentMaxLength = 4000;
const badRequestCode = 400;

const ratingItems = [
    { id: "Bad", key: "Bad", name: "Bad", icon: "thumbs-down", colour: "#F0578A" },
    { id: "OK", key: "OK", name: "OK", icon: "minus", colour: "#FF9076" },
    { id: "Good", key: "Good", name: "Good", icon: "thumbs-up", colour: "#33D1C1" }
];

const validationRules = [
    { fieldName: "comment", required: true, maxLength: commentMaxLength },
    { fieldName: "contactName", maxLength: contactNameMaxLength },
    { fieldName: "phoneNumber", maxLength: phoneNumberMaxLength }
];

const defaultFeedback = {
    rating: null,
    comment: "",
    contactName: "",
    phoneNumber: ""
};

export function LeaveFeedbackModal(
    {
        isOpen = false,
        onClose = defaultFunction
    }
) {
    const source = usePageTitle();
    const [feedback, setFeedback] = useState(defaultFeedback);
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [isValid, errors, validate] = useValidation();

    useEffect(runValidation, [feedback]);
    useEffect(resetOnClose, [isOpen]);

    function runValidation() {
        validate(validationRules, feedback);
    }

    function resetOnClose() {
        if (!isOpen) {
            setFeedback(defaultFeedback);
            setSubmitted(false);
        }
    }

    function onFieldChange(fieldName, value) {
        setFeedback({
            ...feedback,
            [fieldName]: value
        });
    }

    function onSubmitClick() {
        if (!isValid || submitting) {
            return;
        }

        setSubmitting(true);

        FeedbackService.submitFeedback({
            source,
            rating: feedback.rating ?? "",
            comment: feedback.comment,
            contactName: feedback.contactName,
            phoneNumber: feedback.phoneNumber
        })
            .then(() => {
                setSubmitted(true);
            })
            .catch((error) => {
                if (error?.status === badRequestCode) {
                    NotificationToaster.show(Intent.WARNING, error.data);
                } else {
                    NotificationToaster.show(Intent.DANGER, "Your feedback could not be sent. Please try again.");
                }
            })
            .finally(() => {
                setSubmitting(false);
            });
    }

    let modalTitle = "Leave Feedback";

    if (submitted) {
        modalTitle = "Thank you for your feedback";
    }

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            clickOutsideClose={true}
            title={modalTitle}
            size={ModalSize.MEDIUM}
            updateStateOnClose={false}
            footer={(
                <ShowHide
                    evaluator={submitted}
                    show={(
                        <div className="bp6-dialog-footer-actions">
                            <Button
                                id="close-feedback-button"
                                text="Close"
                                intent={Intent.PRIMARY}
                                onClick={onClose}
                            />
                        </div>
                    )}
                    hide={(
                        <div className="bp6-dialog-footer-actions">
                            <Button
                                id="cancel-feedback-button"
                                text="Cancel"
                                intent={Intent.SECONDARY}
                                onClick={onClose}
                            />
                            <ButtonSave
                                id="submit-feedback-button"
                                text="Submit"
                                onClick={onSubmitClick}
                                simpleDisabled={!isValid}
                                disabled={submitting}
                            />
                        </div>
                    )}
                />
            )}
        >
            <ShowHide
                evaluator={submitted}
                show={(
                    <p>Your message has been sent to the Vision development team.</p>
                )}
                hide={(
                    <Fragment>
                        <h4>Rating</h4>
                        <FormToggleList
                            items={ratingItems}
                            value={feedback.rating ?? ""}
                            disabled={submitting}
                            onChange={(key) => onFieldChange("rating", key)}
                        />
                        <FormTextArea
                            id="feedback-comment"
                            headingText="Comment"
                            value={feedback.comment}
                            maxLength={commentMaxLength}
                            showCounter={true}
                            disabled={submitting}
                            dangerHelperText={errors.comment}
                            onChange={(event) => onFieldChange("comment", event.target.value)}
                            fill={true}
                        />
                        <SubmissionPrivacyNotice />
                        <SubmissionContactSection
                            contactName={feedback.contactName}
                            phoneNumber={feedback.phoneNumber}
                            errors={errors}
                            disabled={submitting}
                            onChange={onFieldChange}
                        />
                    </Fragment>
                )}
            />
        </Modal>
    );
}

LeaveFeedbackModal.propTypes = {
    isOpen: PropTypes.bool,
    onClose: PropTypes.func
};
